import { registerSW } from 'virtual:pwa-register';

/**
 * Service Worker registration.
 *
 * History:
 *   - Originally registered immediately on module import.
 *   - Then deferred to `window.load` to avoid disrupting Chrome's PWA
 *     engagement timer.
 *   - Now: registered eagerly, but GUARDED against double-registration.
 *
 * Why the change away from `load` event:
 *   `swAuth.init()` (called from login flow) does
 *   `await navigator.serviceWorker.ready`. That promise resolves only when
 *   the SW becomes the page's controller. If registration is deferred to
 *   `load`, but the user logs in BEFORE `load` fires (fast typing on slow
 *   connection), `swAuth.init()` hangs. That's the bug we just diagnosed:
 *   the breakpoint at `onRegistered` was firing DURING the login API call
 *   because login was waiting for SW readiness that hadn't been kicked off
 *   yet. Eager registration eliminates the race.
 *
 * Why double-registration guard:
 *   We confirmed `PWA: Service Worker registered` was logging twice. Two
 *   possible causes:
 *     1. Vite HMR re-evaluating this module in dev mode.
 *     2. `virtual:pwa-register`'s internal lifecycle.
 *   Either way, calling registerSW() twice creates two registration calls,
 *   confuses Chrome's engagement detection, and can leave a stale `updateSW`
 *   binding on window. The `__rabexPwaRegistered` global flag guards both.
 *
 * Why register eagerly:
 *   The `load` deferral was solving a problem we don't have. Modern Chrome
 *   handles SW registration during page load fine — it doesn't disrupt
 *   FCP/LCP. Registering eagerly fixes the login race in exchange for
 *   marginally earlier SW work, which is a clear win.
 */

declare global {
  interface Window {
    __rabexPwaRegistered?: boolean;
    updateSW?: (reload?: boolean) => Promise<void>;
  }
}

if (typeof window !== 'undefined' && !window.__rabexPwaRegistered) {
  console.log(
    '🔴 GUARD ENTERED — calling registerSW NOW',
    new Date().toISOString()
  );
  window.__rabexPwaRegistered = true;

  const updateSW = registerSW({
    // immediate:true ensures registration starts as soon as registerSW is
    // called, not on next tick. We want the SW lifecycle to kick off NOW
    // so swAuth.init() doesn't hang during login.
    immediate: true,

    onNeedRefresh() {
      window.dispatchEvent(new CustomEvent('pwa:need-refresh'));
    },

    onOfflineReady() {
      window.dispatchEvent(new CustomEvent('pwa:offline-ready'));
    },

    onRegistered(registration) {
      // This fires when the SW successfully registers. Could log multiple
      // times if Chrome re-checks the SW (e.g. on update detection). That's
      // fine — the registration itself only happens once thanks to our
      // guard above.
      if (process.env.NODE_ENV !== 'production') {
        console.log('PWA: Service Worker registered', registration);
      }
    },

    onRegisterError(error) {
      console.error('PWA: Service Worker registration error', error);
    }
  });

  // Expose updateSW globally so PwaNotifications can trigger the update flow
  // via `updateSW(true)` (which posts SKIP_WAITING + reloads the page).
  window.updateSW = updateSW;
}
